import { FC, useEffect } from 'react';

import {
  Box,
  Text,
  Flex,
  Input,
  Radio,
  Center,
  Button,
  Spinner,
  useToast,
  FormLabel,
  RadioGroup,
  InputGroup,
  FormControl,
  InputRightElement,
  Modal,
  useDisclosure,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  Switch,
} from '@chakra-ui/react';
import {
  MdAttachFile,
  MdCalendarToday,
  MdDownload,
  MdUploadFile,
} from 'react-icons/md';

import { Form, Formik, useField, useFormikContext } from 'formik';

import getSortedData from 'utils/getSortedData';
import composeFullAddress from 'utils/composeFullAddress';
import parsePlacesResult, { unitToKeep } from 'utils/parsePlacesResult';

import { convertUTCToLocal } from 'utils/dateUtils';

import { selectors } from 'store/slices/washhub';
import { days, pickupFrequency } from 'constants/weekdays';

import { uploadContract } from 'api/site';
import { getAllItemsForPackages } from 'api/sku';
import { useMutation } from 'react-query';

import { useDropzone } from 'react-dropzone';

import { useNavigate, useLocation } from 'react-router-dom';

import { UrlInput } from 'components/modules/UrlInput';
import { UrlSwitch } from 'components/modules/UrlSwitch';
import { PackageSelect } from 'components/modules/PackageSelect';

import ReactDatePicker from 'react-datepicker';
import FormikInput from 'components/primitives/FormikInput';
import FormikImageInput from 'components/modules/FormikImageInput';
import FormikTextArea from 'components/primitives/FormikTextArea';
import FormikDatepicker from 'components/primitives/FormikDatepicker';
import FormikMultiSelect from 'components/primitives/FormikMultiSelect';
import FormikSelectInput from 'components/primitives/FormikSelectInput';
import FormikSwitch from 'components/primitives/FormikSwitch';
import PlacesAutocomplete from 'components/primitives/PlacesAutocomplete';
import { EmployeeFilterableSelect } from 'components/modules/EmployeeSelect';

import {
  getInitialValues,
  validationSchema,
} from 'components/modules/SiteModal/SiteModal.utils';

import { Site, SiteTypeEnum } from 'types/site';
import { SiteFormProps } from './SiteForm.props';
import FormikCheckBox from 'components/primitives/FormikCheckBox';
import { format, isValid, max } from 'date-fns';

const labelProps = {
  fontSize: '14px',
  color: '#afafaf',
};

const TYPES = Object.values(SiteTypeEnum);
const SiteFormView: FC<SiteFormProps> = ({
  siteId,
  site,
  isReadOnly,
  onSubmit,
  isLoading,
  onEditForm,
}) => {
  const navigate = useNavigate();
  const washHub = selectors.useSelectedWashHub();

  return (
    <Formik
      enableReinitialize
      initialValues={getInitialValues(site)}
      validationSchema={validationSchema}
      onSubmit={onSubmit}>
      {({
        values,
        errors,
        touched,
        isValid,
        initialValues,
        setFieldValue,
        setFieldTouched,
      }) => (
        <Form>
          <Flex p="10px" align="center" justify="space-between" bg="#E7EFEA">
            <Text fontSize="18px" fontWeight="600">
              Site Details
            </Text>

            <Flex gap="10px" align="center">
              <Text>Make this Site Inactive</Text>
              <Switch
                isReadOnly={isReadOnly}
                colorScheme="primary"
                isChecked={values?.isSuspended}
                onChange={(e) => {
                  setFieldValue('isSuspended', e.target.checked);
                }}
              />
            </Flex>
            <Flex
              gap="10px"
              align="center"
              opacity={isReadOnly ? 0.6 : 1}
              pointerEvents={isReadOnly ? 'none' : undefined}>
              <Text>Archive</Text>
              <FormikSwitch name="isArchived" readOnly={isReadOnly} />
            </Flex>
          </Flex>
          <Flex gap="20px">
            <FormikInput
              name="name"
              label="Site Name"
              isRequired
              labelProps={labelProps}
              containerProps={{ flex: 1 }}
              isReadOnly={isReadOnly}
            />
            <Flex
              flex={1}
              pointerEvents={isReadOnly ? 'none' : undefined}
              zIndex={3}>
              <PlacesAutocomplete
                name="address"
                isRequired
                labelText="Address"
                labelProps={{ ...labelProps, fontWeight: 'bold' }}
                defaultInputValue={composeFullAddress(values?.address)}
                isReadOnly={isReadOnly}
                onSelect={(result) => {
                  const parsed = parsePlacesResult(result);

                  // addressLine1 is the street; addressLine2 the unit Google
                  // gave, else the one typed, never a copied address.
                  setFieldValue('address', {
                    ...parsed,
                    zip: parsed.zip || '00000',
                    addressLine2: unitToKeep(
                      parsed.addressLine2,
                      values?.address?.addressLine2,
                    ),
                  });
                }}
              />
            </Flex>
            <FormikSelectInput
              placeholder="Select"
              label="Site Type"
              containerProps={{ flex: 1 }}
              name="siteType"
              isDisabled={isReadOnly}
              labelProps={labelProps}>
              {getSortedData(TYPES)?.map((s) => (
                <option key={s} value={s}>
                  {s || ''}
                </option>
              ))}
            </FormikSelectInput>
            <FormikInput
              name="washHub"
              label="Wash Hub"
              labelProps={labelProps}
              isReadOnly
              value={washHub?.name || ''}
              containerProps={{ flex: 1 }}
            />
          </Flex>

          <Flex gap="20px" mt="20px">
            <FormikSelectInput
              isRequired
              labelProps={labelProps}
              containerProps={{ flex: 1 }}
              placeholder="Select"
              isDisabled={isReadOnly}
              label="Delivery Frequency"
              name="deliveryFrequency">
              {getSortedData(pickupFrequency).map((w) => (
                <option key={w} value={w}>
                  {w || ''}
                </option>
              ))}
            </FormikSelectInput>
            {['Weekly', 'Bi-Weekly', 'Monthly', 'Per Event'].includes(
              values?.deliveryFrequency,
            ) && (
              <>
                <FormikMultiSelect
                  options={days}
                  isReadOnly={isReadOnly}
                  labelProps={labelProps}
                  containerProps={{ flex: 1 }}
                  placeholder="Select"
                  label="Delivery Day Of The Week"
                  name="deliveryDayOfTheWeek"
                />
              </>
            )}
            <FormikInput
              type="number"
              name="capacity"
              label="Capacity"
              labelProps={labelProps}
              containerProps={{ flex: 1 }}
              isReadOnly={isReadOnly}
            />
            <FormikInput
              name="website"
              label="Website Show List"
              placeholder="your URL here"
              labelProps={labelProps}
              containerProps={{ flex: 1 }}
              isReadOnly={isReadOnly}
              type="text"
            />
            <FormikInput
              name="qrLandingUrl"
              label="QR Landing Page URL"
              placeholder="https://..."
              labelProps={labelProps}
              containerProps={{ flex: 1 }}
              isReadOnly={isReadOnly}
              type="text"
            />
          </Flex>

          <Flex gap="20px" mt="20px">
            <Flex direction="column" flex={1} gap="20px">
              <FormikTextArea
                minH="150px"
                name="deliveryInstructions"
                label="Delivery instructions"
                labelProps={labelProps}
                isReadOnly={isReadOnly}
                isDisabled={isReadOnly}
              />
              <FormikTextArea
                minH="150px"
                name="pickupInstructions"
                label="Pickup instructions"
                labelProps={labelProps}
                isReadOnly={isReadOnly}
                isDisabled={isReadOnly}
              />
            </Flex>
            <Flex direction="column" gap="20px" flex={1}>
              <Flex flex={1} gap="20px">
                <Flex flex={1}>
                  <FilePicker
                    siteId={siteId}
                    fieldError={errors.contract}
                    contracts={site?.contracts}
                    isReadOnly={isReadOnly}
                    onEditForm={onEditForm}
                  />
                </Flex>
                <Flex flex={1}>
                  <FormikDatepicker
                    name="contractDate"
                    label="Contract Expiration (*)"
                    labelProps={labelProps}
                    placeholderText="Your text here"
                    readOnly={
                      isReadOnly ||
                      (typeof values.hasExpirationDate === 'boolean'
                        ? !values.hasExpirationDate
                        : false)
                    }
                    error={errors.contractDate}
                    isEditable
                  />
                </Flex>
              </Flex>
              <Flex>
                <Flex flex={0.5}>
                  <FormikCheckBox
                    field="hasExpirationDate"
                    name="Doesn’t have an expiration date"
                    fontSize="20px"
                    isDisabled={isReadOnly}
                    isChecked={
                      typeof values.hasExpirationDate === 'boolean'
                        ? !values.hasExpirationDate
                        : undefined
                    }
                    onChange={(e) => {
                      setFieldValue('hasExpirationDate', !e.target.checked);

                      const inputEl = document.getElementById(
                        'date-picker-input',
                      ) as HTMLInputElement;

                      setTimeout(() => {
                        if (e.target.checked) {
                          setFieldValue('contractDate', null);

                          if (inputEl) {
                            inputEl.value = '';
                          }
                        } else {
                          const newDate = new Date();

                          setFieldValue('contractDate', newDate);

                          if (inputEl) {
                            inputEl.value = format(newDate, 'MM/dd/yyyy');
                          }
                        }
                      }, 50);
                    }}
                  />
                </Flex>
                <Flex flex={0.5}>
                  <EmployeeFilterableSelect
                    name="accountManagers"
                    label="Account Manager"
                    isReadOnly={isReadOnly}
                    isMulti
                    isRequired={false}
                    labelProps={labelProps}
                    getAllEmployees={true}
                    containerProps={{ flex: 1, marginTop: '0px' }}
                  />
                </Flex>
              </Flex>
            </Flex>
          </Flex>

          <Text
            mt="20px"
            p="10px"
            fontSize="18px"
            fontWeight="600"
            bg="#E7EFEA">
            SKU & Invoicing
          </Text>

          <Flex gap="20px">
            <Flex flex={1} mr="20px">
              <PackageSelect
                values={values?.packages}
                washHubId={washHub?._id}
                isReadOnly={isReadOnly}
              />
            </Flex>
            <Flex direction="column" flex={1}>
              <FormControl
                isRequired
                onClick={() => {
                  setFieldTouched('invoice', true);
                }}>
                <FormLabel
                  my="3"
                  {...labelProps}
                  fontWeight="bold"
                  textTransform="capitalize">
                  Invoice Per Delivery ?
                </FormLabel>
                <RadioGroup
                  size="lg"
                  isDisabled={isReadOnly}
                  colorScheme="primary"
                  value={
                    typeof values?.invoice === 'boolean'
                      ? values?.invoice
                        ? '1'
                        : '0'
                      : undefined
                  }
                  onChange={(e) => {
                    setFieldValue('invoice', e === '1' ? true : false);
                  }}>
                  <Radio value="1">Yes</Radio>
                  <Radio value="0" ml="10px">
                    No
                  </Radio>
                </RadioGroup>
                {errors?.invoice && touched.invoice && (
                  <Text fontSize="sm" color="red.500">
                    {errors?.invoice}
                  </Text>
                )}
              </FormControl>
            </Flex>
            <Flex direction="column" flex={1}>
              <FormControl
                isRequired
                onClick={() => {
                  setFieldTouched('invoicePerPickup', true);
                }}>
                <FormLabel
                  my="3"
                  {...labelProps}
                  fontWeight="bold"
                  textTransform="capitalize">
                  Invoice Per Pickup ?
                </FormLabel>
                <RadioGroup
                  size="lg"
                  isDisabled={isReadOnly}
                  colorScheme="primary"
                  value={
                    typeof values?.invoicePerPickup === 'boolean'
                      ? values?.invoicePerPickup
                        ? '1'
                        : '0'
                      : undefined
                  }
                  onChange={(e) => {
                    setFieldValue('invoicePerPickup', e === '1' ? true : false);
                  }}>
                  <Radio value="1">Yes</Radio>
                  <Radio value="0" ml="10px">
                    No
                  </Radio>
                </RadioGroup>
                {errors?.invoicePerPickup && touched.invoicePerPickup && (
                  <Text fontSize="sm" color="red.500">
                    {errors?.invoicePerPickup}
                  </Text>
                )}
              </FormControl>
            </Flex>

            <Flex direction="column" flex={1}>
              <FormControl
                isRequired
                onClick={() => {
                  setFieldTouched('hasCokeAdjustment', true);
                }}>
                <FormLabel
                  my="3"
                  {...labelProps}
                  fontWeight="bold"
                  textTransform="capitalize">
                  Coke Adjustment ?
                </FormLabel>
                <RadioGroup
                  size="lg"
                  isDisabled={isReadOnly}
                  colorScheme="primary"
                  value={
                    typeof values?.hasCokeAdjustment === 'boolean'
                      ? values?.hasCokeAdjustment
                        ? '1'
                        : '0'
                      : undefined
                  }
                  onChange={(e) => {
                    setFieldValue(
                      'hasCokeAdjustment',
                      e === '1' ? true : false,
                    );
                  }}>
                  <Radio value="1">Yes</Radio>
                  <Radio value="0" ml="10px">
                    No
                  </Radio>
                </RadioGroup>
                {errors?.hasCokeAdjustment && touched.hasCokeAdjustment && (
                  <Text fontSize="sm" color="red.500">
                    {errors?.hasCokeAdjustment}
                  </Text>
                )}
              </FormControl>
            </Flex>
          </Flex>

          <ItemsList isReadOnly={isReadOnly} />

          <Flex gap="20px" mt="20px">
            <FormikTextArea
              name="invoiceNotes"
              label="Invoice Notes"
              labelProps={labelProps}
              isReadOnly={isReadOnly}
              isDisabled={isReadOnly}
              infoText="Add any specific notes or instructions related to the invoice here. This could include special payment terms, additional invoicing details, or other relevant information that aids in invoice processing."
            />
            <FormikTextArea
              name="billingDetails"
              label="Billing Details"
              labelProps={labelProps}
              isReadOnly={isReadOnly}
              isDisabled={isReadOnly}
              infoText="Enter your billing information here. This includes details like billing address, tax information, and any specific billing instructions or references. Ensure accuracy to avoid any invoicing issues."
            />
          </Flex>

          <Text
            mt="20px"
            p="10px"
            fontSize="18px"
            fontWeight="600"
            bg="#E7EFEA">
            Partner Portal
          </Text>

          <Flex gap="20px">
            <Flex flex={1}>
              <UrlSwitch
                name="portal"
                isReadOnly={isReadOnly}
                onChange={(e) => {
                  if (e.target.checked === false) {
                    if (!!initialValues.portalName && !values.portalName) {
                      setFieldValue('portalName', initialValues.portalName);
                    }
                  }
                }}
              />
            </Flex>
            {/* Not gated on the `portal` toggle: the logo is shown on the
                public ordering form (/cms/:portalName), which is driven by
                portalName rather than by partner portal access. */}
            <FormikImageInput
              name="logo"
              label="Logo"
              siteId={siteId}
              aspectRatio={1 / 1}
              containerProps={{ flex: 1 }}
              isDisabled={isReadOnly}
              labelProps={labelProps}
            />
          </Flex>

          <UrlInput name="portalName" isReadOnly={isReadOnly} />

          {isReadOnly ? (
            <Box h="100px" />
          ) : (
            <Flex my={12} flexDirection="row" justifyContent="center">
              <Button
                variant="outline"
                marginRight="10px"
                w="134px"
                onClick={() => navigate(-1)}
                isDisabled={isLoading || values._isUploading}>
                Cancel
              </Button>
              <Button
                isDisabled={!isValid}
                w="134px"
                type="submit"
                isLoading={isLoading || values._isUploading}>
                {!!site ? 'Update Site' : 'Create Site'}
              </Button>
            </Flex>
          )}
        </Form>
      )}
    </Formik>
  );
};

export default SiteFormView;

const FilePicker: FC<{
  siteId: string;
  fieldError?: string;
  contracts?: Site['contracts'];
  isReadOnly: boolean;
  onEditForm?: () => void;
}> = ({ siteId, fieldError, contracts, isReadOnly, onEditForm }) => {
  const toast = useToast();
  const location = useLocation();
  const isCreateSite = location.pathname.includes('/create');

  const { isOpen, onOpen, onClose } = useDisclosure();

  const [{ value }, , helpers] = useField('contract');

  const { mutate, isLoading } = useMutation(
    (_file: File) => uploadContract(_file, siteId),
    {
      onError: (e: Error) => {
        toast({
          status: 'error',
          title: e?.message,
          duration: 3000,
          isClosable: true,
        });
      },
      onSuccess: (fileName) => {
        helpers.setValue(fileName);
      },
    },
  );

  const { getInputProps, getRootProps, open } = useDropzone({
    onDrop: ([file]) => {
      if (file) {
        mutate(file);
      }
    },
  });

  const lastUploadedContract = max(
    (contracts || [])
      ?.filter((c) => !!c.uploadedAt && !!c.contract)
      ?.map((c) => new Date(c.uploadedAt || '')),
  );

  return (
    <Flex direction="column" flex={1} align="flex-start">
      <FormControl mt="3" pointerEvents={isReadOnly ? 'none' : undefined}>
        <FormLabel {...labelProps} textTransform="capitalize">
          Contract
        </FormLabel>
        <InputGroup {...getRootProps()} position="relative">
          <Input
            isReadOnly
            isDisabled={isLoading}
            value={
              isReadOnly
                ? isValid(lastUploadedContract)
                  ? `Uploaded Date: ${
                      convertUTCToLocal(
                        lastUploadedContract,
                        true,
                        true,
                      ) as string
                    }`
                  : 'No Contract Uploaded'
                : value
            }
            isRequired
          />
          <input {...getInputProps()} disabled={isLoading} />
          <InputRightElement transform="rotate(90deg)">
            <MdAttachFile color="primary.500" size="18px" />
          </InputRightElement>

          {isLoading && <Spinner position="absolute" top="2" left="2" />}
        </InputGroup>

        {fieldError && (
          <Text color="red.500" fontSize="sm">
            {fieldError}
          </Text>
        )}
      </FormControl>
      {isCreateSite ? (
        <Text fontSize="sm" mt="5px" fontWeight="600" color="primary.500">
          Attach Contract
        </Text>
      ) : (
        <Text
          as="button"
          type="button"
          fontSize="sm"
          fontWeight="600"
          mt="5px"
          cursor="pointer"
          color="primary.500"
          onClick={onOpen}>
          See All Contracts
        </Text>
      )}
      {isCreateSite ? null : (
        <Modal isCentered size="2xl" isOpen={isOpen} onClose={onClose}>
          <ModalOverlay />
          <ModalContent borderRadius="20px">
            <ModalCloseButton top="25px" />
            <ModalHeader
              shadow="lg"
              pr="80px"
              py="20px"
              pl="40px"
              display="flex"
              justifyContent="space-between">
              <Text>Previous contracts</Text>
              <Button
                leftIcon={<MdUploadFile />}
                onClick={() => {
                  onEditForm?.();
                  onClose();
                  open();
                }}>
                Upload New Contract
              </Button>
            </ModalHeader>
            <ModalBody>
              <Flex flex={1} m="50px" direction="column" color="support.500">
                <Flex p="20px" flex={1} bg="surface.400">
                  <Text flex={1} fontWeight="bold">
                    Contract
                  </Text>
                  <Text flex={1} fontWeight="bold">
                    Date Uploaded
                  </Text>
                  {/* <Text flex={1} fontWeight="bold">
                    Date Uploaded
                  </Text> */}
                  <Text opacity={0}>
                    <MdDownload />
                  </Text>
                </Flex>
                {contracts?.filter((c) => !!c.contract)?.length === 0 ? (
                  <Flex p="20px" flex={1} bg="surface.200">
                    <Text flex={1} fontWeight="600">
                      No Contract Uploaded
                    </Text>
                  </Flex>
                ) : null}
                {contracts
                  ?.filter((c) => !!c.contract)
                  ?.map((item, index) => {
                    const name = item.contract?.split('/')?.pop();
                    const fileSource = `https://rturn${
                      process.env.REACT_APP_ENVIRONMENT === 'dev' ? '-dev' : ''
                    }.s3.amazonaws.com/${item.contract}`;

                    return (
                      <Flex
                        key={item._id}
                        p="20px"
                        flex={1}
                        bg={index % 2 === 0 ? 'surface.200' : 'surface.100'}>
                        <Text flex={1} fontWeight="600">
                          {name}
                        </Text>
                        <Text flex={1} fontWeight="600">
                          {item?.uploadedAt
                            ? convertUTCToLocal(
                                new Date(item?.uploadedAt),
                                true,
                                true,
                              )
                            : '-'}
                        </Text>
                        <Text
                          as="a"
                          href={fileSource}
                          download={name}
                          target="_blank"
                          type="button">
                          <MdDownload />
                        </Text>
                      </Flex>
                    );
                  })}
              </Flex>
            </ModalBody>
          </ModalContent>
        </Modal>
      )}
    </Flex>
  );
};

const ItemsList: FC<{ isReadOnly?: boolean }> = ({ isReadOnly }: any) => {
  const { values, setFieldValue } = useFormikContext<any>();
  const packages: string[] = values?.packages || [];
  const pricingDetails: Site['pricingDetails'] = values?.pricingDetails;

  const { data = [], isLoading, mutate } = useMutation(
    () => getAllItemsForPackages(packages),
    {
      onSuccess: (res) => {
        setFieldValue(
          'pricingDetails',
          res.map((s) => {
            const savedItem = ((values.pricingDetails || []) as any[]).find(
              (p) => p.itemSku === s._id,
            );

            return {
              itemSku: savedItem?.itemSku || s._id,
              deliveryPrice:
                savedItem?.deliveryPrice ?? s?.pricePerDelivery ?? 0,
              replacementPrice:
                savedItem?.replacementPrice ?? s?.pricePerReplacement ?? 0,
              effectiveDate: savedItem?.effectiveDate || null,
            };
          }),
        );
      },
    },
  );

  useEffect(() => {
    if (packages.length === 0) {
      return;
    }

    mutate();
  }, [packages.toString()]);

  return (
    <Flex my="25px" borderRadius="5px" overflow="hidden" direction="column">
      <Flex flex={1} p="10px" bg="surface.400" gap="15px">
        <Text flex={1} fontWeight="600" fontSize="14px" color="support.500">
          Item SKU
        </Text>
        <Text flex={1} fontWeight="600" fontSize="14px" color="support.500">
          Delivery Price
        </Text>
        <Text flex={1} fontWeight="600" fontSize="14px" color="support.500">
          Replacement Price
        </Text>
        <Text flex={1} fontWeight="600" fontSize="14px" color="support.500">
          Effective Date
        </Text>
      </Flex>

      {isLoading ? (
        <Center flex={1} p="20px">
          <Spinner />
        </Center>
      ) : packages.length === 0 ? null : (
        <>
          {data?.map((sku, index) => {
            const item = pricingDetails?.find((p) => p.itemSku === sku._id);

            return (
              <Flex
                key={sku._id}
                flex={1}
                p="10px"
                gap="15px"
                bg={index % 2 === 0 ? 'surface.200' : 'surface.100'}>
                <Text
                  flex={1}
                  fontWeight="600"
                  fontSize="14px"
                  color="support.500">
                  {sku?.name || ''}
                </Text>
                <Flex
                  flex={1}
                  fontWeight="600"
                  fontSize="14px"
                  color="support.500">
                  <Input
                    step="0.01"
                    type="number"
                    bg="#fff"
                    maxW="120px"
                    defaultValue={item?.deliveryPrice || 0}
                    onChange={(e) => {
                      setFieldValue(
                        `pricingDetails[${index}].deliveryPrice`,
                        Number(e.target.value),
                      );
                      setFieldValue(
                        `pricingDetails[${index}].effectiveDate`,
                        new Date().toUTCString(),
                      );
                    }}
                    isDisabled={isReadOnly}
                  />
                </Flex>
                <Flex
                  flex={1}
                  fontWeight="600"
                  fontSize="14px"
                  color="support.500">
                  <Input
                    step="0.01"
                    type="number"
                    bg="#fff"
                    maxW="120px"
                    defaultValue={item?.replacementPrice || 0}
                    onChange={(e) => {
                      setFieldValue(
                        `pricingDetails[${index}].replacementPrice`,
                        Number(e.target.value),
                      );

                      setFieldValue(
                        `pricingDetails[${index}].effectiveDate`,
                        new Date().toUTCString(),
                      );
                    }}
                    isDisabled={isReadOnly}
                  />
                </Flex>
                <Flex
                  flex={1}
                  fontWeight="600"
                  fontSize="14px"
                  color="support.500">
                  <ReactDatePicker
                    onChange={(date) => {
                      setFieldValue(
                        `pricingDetails[${index}].effectiveDate`,
                        date?.toUTCString(),
                      );
                    }}
                    customInput={
                      <InputGroup maxW="200px">
                        <Input
                          bg="#fff"
                          value={
                            convertUTCToLocal(
                              new Date(item?.effectiveDate || ''),
                              true,
                              true,
                            ) as string
                          }
                          readOnly
                        />
                        <InputRightElement zIndex={0}>
                          <MdCalendarToday fill="#2FB64A" />
                        </InputRightElement>
                      </InputGroup>
                    }
                  />
                </Flex>
              </Flex>
            );
          })}
        </>
      )}
    </Flex>
  );
};
